import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, Platform, ScrollView, Dimensions, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { getData } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import { getShadow } from '../../theme/useTheme';
import Skeleton from '../common/Skeleton';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const EventPassbook = () => {
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigation = useNavigation();

  useEffect(() => {
    fetchPassbook();
  }, []);

  const fetchPassbook = async () => {
    try {
      setLoading(true);
      const data = await getData('/events/passbook');
      if (data && Array.isArray(data) && data.length > 0) {
        // Filter for upcoming and sort by date
        const now = new Date();
        const upcoming = data
          .filter(m => new Date(m.event?.event_date) >= now)
          .sort((a, b) => new Date(a.event?.event_date) - new Date(b.event?.event_date));
        
        // Show the most imminent upcoming mission, or the latest completed one if none upcoming
        if (upcoming.length > 0) {
          setMission(upcoming[0]);
        } else {
          // If no upcoming, show the most recent completed one
          const completed = data.sort((a, b) => new Date(b.event?.event_date) - new Date(a.event?.event_date));
          setMission(completed[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching passbook:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <View style={styles.container}>
      <Skeleton width="100%" height={70} borderRadius={14} />
    </View>
  );

  if (!mission || !mission.event) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyWrapper}>
          <View style={styles.emptyContainer}>
            <Ionicons name="calendar-outline" size={20} color="#9CA3AF" />
            <Text style={styles.emptyText}>Events you join will appear here</Text>
          </View>
        </View>
      </View>
    );
  }

  const evt = mission.event;
  const eventDate = new Date(evt.event_date);
  const dateStr = eventDate.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short', 
    day: 'numeric',
    year: eventDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
  const isCompleted = new Date(evt.event_date) < new Date();

  return (
    <View style={styles.container}>
      <TouchableOpacity
        onPress={() => navigation.navigate('EventDetails', { eventId: evt.id })}
        activeOpacity={0.8}
        style={styles.passCard}
      >
        <View style={styles.cardRow}>
          {/* Image Section */}
          <View style={styles.imageContainer}>
            <Image 
              source={{ uri: getImageUrl(evt.image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800' }} 
              style={styles.cardImage} 
              resizeMode="cover"
            />
          </View>
          
          {/* Content Section */}
          <View style={styles.contentContainer}>
            <Text style={styles.vibeText}>{evt.vibe?.toUpperCase() || 'SKILL UP'}</Text>
            <Text style={styles.titleText} numberOfLines={2}>{evt.title}</Text>
            
            <View style={styles.detailRow}>
              <View style={styles.metaItem}>
                <Ionicons name="calendar-outline" size={12} color="#4F46E5" />
                <Text style={styles.metaText}>{dateStr}</Text>
                {(() => {
                  const now = new Date();
                  const start = evt.start_time ? new Date(evt.start_time) : new Date(evt.event_date);
                  const end = evt.end_time ? new Date(evt.end_time) : new Date(new Date(evt.event_date).getTime() + 2 * 60 * 60 * 1000);
                  
                  if (now > end) {
                    return <Text style={[styles.statusText, { color: '#10B981' }]}>• Completed</Text>;
                  } else if (now >= start && now <= end) {
                    return <Text style={[styles.statusText, { color: '#10B981' }]}>• Ongoing</Text>;
                  }
                  return null;
                })()}
              </View>
              
              <View style={styles.xpItem}>
                <Ionicons name="star" size={12} color="#F59E0B" />
                <Text style={styles.xpText}>+{evt.points_reward || 5} XP</Text>
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    marginVertical: 10,
  },
  passCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    ...getShadow('sm'),
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  cardRow: {
    flexDirection: 'row',
    height: 115,
  },
  imageContainer: {
    width: '42%',
    height: '100%',
  },
  cardImage: {
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    flex: 1,
    padding: 12,
    justifyContent: 'center',
    gap: 3,
  },
  vibeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#4F46E5',
    letterSpacing: 0.3,
    marginBottom: 1,
  },
  titleText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 6,
    lineHeight: 18,
  },
  detailRow: {
    gap: 4,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    color: '#374151',
    fontSize: 11,
    fontWeight: '600',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '700',
    marginLeft: 4,
  },
  xpItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 1,
  },
  xpText: {
    color: '#F59E0B',
    fontWeight: '800',
    fontSize: 11,
  },
  emptyWrapper: {
    width: '100%',
  },
  emptyContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    borderStyle: 'dashed',
  },
  emptyText: {
    color: '#94A3B8',
    fontSize: 13,
  }
});

export default EventPassbook;
